import { router, useLocalSearchParams } from 'expo-router';
import { useEffect } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { Divider } from 'react-native-paper';

import { useCreateManyAmenities } from '@/api/hooks/amenitiesHooks';
import AddAnotherButton from '@/components/buttons/AddAnotherButton';
import BrandedButton from '@/components/buttons/BrandedButton';
import AmenityForm from '@/components/forms/AmenityForm';
import FormWrapper from '@/components/forms/FormWrapper';
import FieldArrayListItemAmenityCard from '@/components/npo/amenities/FieldArrayListItemAmenityCard';
import Text from '@/components/Text';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { AmenitiesData, amenitiesResolver, AmenityData } from '@/lib/validations/AmenitiesSchema';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';
import { objectComparision } from '@/utils/objectComparision';

const Amenities = () => {
  const { state } = useAuth();
  const npoId = state.signUpData?.loginEmail?.npoId;

  const { initialList, amenityDataJson, amenityIndex } = useLocalSearchParams<{
    initialList: string;
    amenityDataJson: string;
    amenityIndex: string;
  }>();
  const { showSuccess } = useEndFlowModal();

  useEffect(() => {
    if (amenityDataJson) {
      const amenityData = safeJsonParse<AmenityData>(amenityDataJson);
      if (amenityData) {
        // eslint-disable-next-line no-unused-expressions
        !amenityIndex && !fields.find((field) => objectComparision(field, amenityData))
          ? append(amenityData)
          : update(+amenityIndex, amenityData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amenityDataJson]);

  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    clearErrors,
    formState: { isValid, submitCount, errors }
  } = useForm<AmenitiesData>({
    defaultValues: { amenities: initialList !== undefined ? safeJsonParse<AmenityData[]>(initialList) : [] },
    mode: 'onSubmit',
    resolver: amenitiesResolver
  });

  const { fields, insert, append, update, remove } = useFieldArray({
    control,
    name: 'amenities'
  });

  const { mutateAsync: createManyAmenities, isPending } = useCreateManyAmenities();

  const onSubmit: SubmitHandler<AmenitiesData> = async (data) => {
    if (npoId) {
      const mappedData = data.amenities.map((amenity) => ({
        title: amenity.title,
        nonProfitOrgId: npoId
      }));

      const body = {
        amenities: mappedData
      };

      createManyAmenities(body, {
        onSuccess: () => {
          showSuccess({
            title: i18n.t('end_flow.success_title'),
            message: i18n.t('profile.create_form.amenities_success_message')
          });
          router.back();
        },
        onError: (e) => {
          console.error(e);
        }
      });
    }
  };

  return (
    <FormWrapper scrollEnable>
      <View style={styles.container}>
        {getValues('amenities') && getValues('amenities').length === 0 ? (
          <View style={styles.container}>
            <AmenityForm
              initialData={getValues('amenities.0')}
              showDoneButton
              onDone={(data) => {
                insert(0, data);
                trigger('amenities.0').then(() => {
                  trigger('amenities').then((isArrayValid) => {
                    if (isArrayValid) onSubmit(getValues());
                  });
                });
              }}
              onAdd={(data) => {
                clearErrors();
                update(0, data);
                trigger('amenities.0').then((isValid) => {
                  if (isValid) {
                    router.navigate('/create/addUpdateAmenity');
                  }
                });
              }}
              doneLabel={i18n.t('mix.next')}
            />
          </View>
        ) : (
          <View>
            <Divider style={{ marginBottom: 15 }} />
            <FlatList
              scrollEnabled={false}
              data={fields}
              keyExtractor={(program) => program.id}
              renderItem={(field) => (
                <FieldArrayListItemAmenityCard
                  onPress={() => {
                    router.navigate({
                      pathname: '/create/addUpdateAmenity',
                      params: {
                        amenityDataJson: JSON.stringify(field.item),
                        amenityIndex: field.index
                      }
                    });
                  }}
                  onDelete={() => {
                    remove(field.index);
                  }}
                  title={field.item.title ?? ''}
                />
              )}
              ListFooterComponent={
                <AddAnotherButton
                  onPress={() => router.navigate('/create/addUpdateAmenity')}
                  label={i18n.t('profile.create_form.amenities_add_another')}
                />
              }
            />
          </View>
        )}

        {errors.root && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

        <View style={styles.buttonsContainer}>
          {getValues('amenities') && getValues('amenities').length > 0 && (
            <BrandedButton loading={isPending} disabled={!isValid && submitCount > 0} onPress={handleSubmit(onSubmit)}>
              {i18n.t('mix.done')}
            </BrandedButton>
          )}
        </View>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  fields: {
    flexDirection: 'column',
    gap: 25
  },

  buttonsContainer: {
    flexDirection: 'column'
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  }
});

export default Amenities;
